import type { CreateJobRequestData, JobRequest } from '@/services/api'

/**
 * Qué se copia al duplicar una solicitud de personal (SRH-318).
 *
 * Cuando una plaza se cierra porque se contrató a alguien y hace falta otra
 * igual —otro desarrollador con el mismo perfil, posiciones repetitivas en
 * operaciones— hasta ahora había que llenar la solicitud completa a mano.
 *
 * La copia se arma con LISTA BLANCA: solo viaja lo que se nombra aquí. Con una
 * lista de «todo menos esto», una columna que se agregue mañana a la solicitud
 * —un monto, un comentario interno— se colaría en silencio al duplicado, y la
 * compensación es justo lo que no debe pasar (SRH-319: el solicitante no la ve).
 */

export interface DatosDeSolicitudDuplicada {
  datos: Partial<CreateJobRequestData>
  /** Folio de la original, para decirle a la persona de dónde viene. */
  folioDeOrigen: string
}

/** La clave con la que viaja en el estado de navegación. */
export const CLAVE_DE_DUPLICADO = 'duplicadaDe'

/**
 * Lo que NO se copia, y por qué. No se usa para filtrar —la copia es lista
 * blanca— sino para que las pruebas fijen cada exclusión con su razón:
 *
 * - Compensación (`salarioMinimo`, `salarioMaximo`, `prestaciones`,
 *   comentarios de asignación): la asigna RH y la aprueba el jefe de área. Al
 *   duplicar vuelve a asignarse, no se hereda una oferta ya autorizada.
 * - `fechaRequeridaInicio`: la de la original ya pasó, y el formulario exige
 *   hoy o futuro. Copiarla obligaría a corregir una fecha vencida.
 * - `previousEmployeeName`: es a quién reemplazaba la plaza original. En la
 *   nueva es otra persona, o ninguna.
 * - `internalCandidates`: son candidatos de aquella plaza. La nueva vive el
 *   flujo desde cero, sin candidatos ni historial.
 * - Área: el alta manda siempre el área de quien captura, no la de la original.
 */
export const NO_SE_COPIA = [
  'salarioMinimo',
  'salarioMaximo',
  'prestaciones',
  'comentariosAsignacionSalarios',
  'fechaRequeridaInicio',
  'previousEmployeeName',
  'internalCandidates',
  'areaId',
  'areaVacante',
] as const

export function datosParaDuplicar(original: JobRequest): Partial<CreateJobRequestData> {
  const o = original as unknown as Record<string, unknown>

  return {
    puestoSolicitar: original.puestoSolicitar ?? '',
    sede: original.sede ?? '',
    tipoPerfil: original.tipoPerfil as CreateJobRequestData['tipoPerfil'],
    tipoContrato: original.tipoContrato,
    habilidadesMinimas: original.habilidadesMinimas ?? '',
    motivo: original.motivo,
    prioridad: original.prioridad,
    numeroPlazas: original.numeroPlazas || 1,
    justificacionNecesidad: original.justificacionNecesidad ?? '',
    gradoAcademico: original.gradoAcademico,
    añosExperiencia: original.añosExperiencia ?? undefined,
    entrevistaFinalSolicitante: Boolean(original.entrevistaFinalSolicitante),
    requiresHardwareSoftware: Boolean(original.requiresHardwareSoftware),
    requiresWorkstation: Boolean(original.requiresWorkstation),
    costCenter: original.costCenter ?? '',
    travelPercentage: original.travelPercentage ?? null,
    otraInformacion: original.otraInformacion ?? '',
    // El formulario maneja el proyecto como texto; nulo es «sin proyecto».
    proyectoId: (o.proyectoId as string | null | undefined) ?? '',
    certificacionesRequeridas: original.certificacionesRequeridas ?? [],
    otrasCertificaciones: original.otrasCertificaciones ?? '',
    englishLevel: original.englishLevel ?? null,
    /**
     * Solo nombre y nivel. Las filas guardadas traen también `id` y
     * `jobRequestId`, y el servidor rechaza cualquier campo que no conozca.
     */
    requiredSoftware: (original.requiredSoftware ?? []).map((s) => ({
      name: s.name,
      level: s.level,
    })),
  }
}

/**
 * El folio es derivado del ID, igual que en el servidor (`VAC-0034`). El tipo
 * del front no lo declara, y armarlo aquí evita depender de que el listado lo
 * traiga.
 */
export const folioDe = (id: number | string): string =>
  `VAC-${String(id).padStart(4, '0')}`

export function solicitudDuplicada(original: JobRequest): DatosDeSolicitudDuplicada {
  return {
    datos: datosParaDuplicar(original),
    folioDeOrigen: folioDe(original.id),
  }
}
